/**
 * SB6-II-01 — TanStack Query hooks for the inspection-instructions list.
 *
 * Hierarchical keys + per-query `staleTime`. Reuses `projectsFetch`
 * (the projects/v1 namespace fetch helper) since the resource lives
 * under that domain (OQ-II-0). Mirrors `useProjects.ts` / `useContracts.ts`.
 */
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@aeriosiq/auth-client";
import { projectsFetch } from "../projects/projectsFetch.js";
import { iiKeys } from "./keys.js";
import type {
  IiDetail,
  IiListResponse,
  InspectionInstructionState,
  ListInspectionInstructionsParams,
} from "./types.js";

export function listInspectionInstructionsUrl(
  params: ListInspectionInstructionsParams,
): string {
  const sp = new URLSearchParams();
  sp.set("page", String(params.page ?? 1));
  sp.set("pageSize", String(params.pageSize ?? 50));
  if (params.q && params.q.length > 0) sp.set("q", params.q);
  if (params.projectIds && params.projectIds.length > 0) {
    sp.set("projectIds", params.projectIds.join(","));
  }
  if (params.siteIds && params.siteIds.length > 0) {
    sp.set("siteIds", params.siteIds.join(","));
  }
  if (params.assetIds && params.assetIds.length > 0) {
    sp.set("assetIds", params.assetIds.join(","));
  }
  if (params.states && params.states.length > 0) {
    sp.set("states", params.states.join(","));
  }
  // AC2 — explicit on the wire so the URL is unambiguous in logs. The
  // BE defaults to false too (hides COMPLETED/WITHDRAWN/REJECTED).
  sp.set("showCompleted", params.showCompleted ? "true" : "false");
  if (params.sortBy) sp.set("sortBy", params.sortBy);
  if (params.sortDir) sp.set("sortDir", params.sortDir);
  // TNCY-8 (projects/v1@2.6.0): scope=member returns the cross-tenant
  // assigned-out ∪ grant-exposed IIs, deduped server-side. Only sent when
  // explicitly `member` — omitting it leaves the BE on its `own` default.
  if (params.scope === "member") sp.set("scope", "member");
  // FCII (projects/v1@2.23.0) — orphan filter. Only the two engaging values are
  // sent; omitting leaves the BE on its `all` default.
  if (params.linkage) sp.set("linkage", params.linkage);
  return `/inspection-instructions?${sp.toString()}`;
}

export function useInspectionInstructionsListQuery(
  params: ListInspectionInstructionsParams,
) {
  return useQuery<IiListResponse>({
    queryKey: iiKeys.list(params),
    queryFn: () =>
      projectsFetch<IiListResponse>(listInspectionInstructionsUrl(params)),
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * DI-2-3 — the caller's OWN instructions on one turbine, in the given states
 * (the intake's "which instruction?" question). Same URL builder and fetch
 * helper as the list page; `scope` is left on the BE default `own` — the
 * intake never lands data in another tenant (DI-D8).
 *
 * `enabled`-gated on tenant readiness AND an asset id: a tenant-scoped call
 * fired before the slug resolves goes out without `X-Tenant-ID` and 400s
 * (the repo-wide rule the time-tracking hooks follow).
 */
export function useInspectionInstructionsForAssetQuery(
  assetId: string | null,
  states: readonly InspectionInstructionState[],
  pageSize = 50,
) {
  const tenantReady = useAuthStore((s) => Boolean(s.tenant?.tenantSlug));
  const params: ListInspectionInstructionsParams = {
    assetIds: assetId ? [assetId] : [],
    states,
    showCompleted: false,
    pageSize,
    sortBy: "plannedDate",
    sortDir: "asc",
  };
  return useQuery<IiListResponse>({
    queryKey: iiKeys.list(params),
    queryFn: () =>
      projectsFetch<IiListResponse>(listInspectionInstructionsUrl(params)),
    enabled: tenantReady && typeof assetId === "string" && assetId.length > 0,
    staleTime: 15_000,
    retry: false,
  });
}

export function useInspectionInstructionDetailQuery(id: string | null) {
  return useQuery<IiDetail>({
    queryKey: iiKeys.detail(id),
    enabled: typeof id === "string" && id.length > 0,
    queryFn: () =>
      projectsFetch<IiDetail>(
        `/inspection-instructions/${encodeURIComponent(id ?? "")}`,
      ),
    staleTime: 0,
    retry: false,
  });
}
